/**
 * Легенда (экспликация) указателей на картах с планами (docs/wayfinding/legend.md): какие типы
 * указателей стоят на плане и сколько их. Одна модель на три места — канвас редактора,
 * публичную карту и листы выгрузки: подсчёт, порядок строк и пропорции общие, иначе легенда
 * на экране и на распечатке разошлись бы.
 *
 * Строка — тип носителя (`WayfindingSignType`), а не слой: слой указателей бывает без типа,
 * и указатели разных типов живут в одном слое. Указатели без типа — одной строкой в конце.
 */
import type { WayfindingExportLanguage, WayfindingSignType } from '../types/wayfinding.js';
import { compareStringsNaturalSort } from './importSortOrder.js';
import { readWayfindingNavigation } from './wayfindingSignTypes.js';

/**
 * Тексты легенды — одни на канвас и лист выгрузки, иначе экран и распечатка разошлись бы
 * («Count» на канвасе и «Qty» в PDF). Заголовки свои, карте не задаются (✏️ 20.10.26, Паша):
 * у общей легенды прямо в заголовке сказано, что она по всем планам. Шапка столбцов — как в
 * экспликациях, сетки таблицы нет.
 */
export interface WayfindingLegendTexts {
  title: string;
  summaryTitle: string;
  /** Подпись строки указателей без типа (или со ссылкой на удалённый тип). */
  untyped: string;
  /** Шапка столбцов: знак, название, количество. */
  columns: readonly [string, string, string];
}

const LEGEND_TEXTS: Record<WayfindingExportLanguage, WayfindingLegendTexts> = {
  ru: {
    title: 'Экспликация указателей',
    summaryTitle: 'Экспликация указателей — все планы',
    untyped: 'Без типа',
    columns: ['Усл. обозн.', 'Наименование', 'Кол-во'],
  },
  en: {
    title: 'Sign legend',
    summaryTitle: 'Sign legend — all plans',
    untyped: 'No type',
    columns: ['Symbol', 'Name', 'Qty'],
  },
};

/** Тексты легенды на языке интерфейса или выгрузки; незнакомый язык — русский. */
export function wayfindingLegendTexts(language: string | undefined): WayfindingLegendTexts {
  return language === 'en' ? LEGEND_TEXTS.en : LEGEND_TEXTS.ru;
}

export interface WayfindingLegendSettings {
  /** Легенды на канвасе — справа от каждого плана и общая под колонкой. Нет ключа — показаны. */
  canvas: boolean;
  /**
   * Размер легенды на канвасе, % (✏️ 20.10.26): множитель, легенда остаётся в масштабе плана.
   * Только канвас: на листе выгрузки кегль — доля листа. Нет ключа — `WAYFINDING_LEGEND_SCALE_DEFAULT`.
   */
  scale: number;
}

/** Ползунок «Размер легенды» в его пределах. */
export function clampWayfindingLegendScale(value: number): number {
  return Math.min(WAYFINDING_LEGEND_SCALE_MAX, Math.max(WAYFINDING_LEGEND_SCALE_MIN, value));
}

/**
 * Настройки легенды из `settings.navigation`: плоские ключи `canvasLegend` и `legendScale` —
 * слияние правок поддерева идёт по ключам верхнего уровня (`mergeNavigationChange`).
 */
export function readWayfindingLegendSettings(settings: unknown): WayfindingLegendSettings {
  const nav = readWayfindingNavigation(settings);
  const scale =
    typeof nav.legendScale === 'number' && Number.isFinite(nav.legendScale)
      ? clampWayfindingLegendScale(nav.legendScale)
      : WAYFINDING_LEGEND_SCALE_DEFAULT;
  return { canvas: nav.canvasLegend !== false, scale };
}

/**
 * Подпись строки типа: «код — название», как в экспликациях; без кода — название. Название,
 * которое уже начинается с кода («8-6» у типа с кодом «8-6», «130_M Номер кабинета»), не
 * повторяет его второй раз.
 */
export function wayfindingLegendTypeLabel(type: Pick<WayfindingSignType, 'name' | 'code'>): string {
  const name = type.name.trim();
  const code = type.code?.trim();
  if (!code) {
    return name;
  }
  if (!name) {
    return code;
  }
  return name.toLocaleLowerCase().startsWith(code.toLocaleLowerCase()) ? name : `${code} — ${name}`;
}

/** Сколько указателей одного типа стоит на плане. `signTypeId: null` — без типа. */
export interface WayfindingLegendCount {
  frameId: string;
  signTypeId: string | null;
  count: number;
}

/**
 * Счёт публичной карты: по слою, чтобы зритель, погасивший слой глазом, увидел легенду без
 * него. Тип уже сверен сервером со справочником (`resolveWayfindingSignTypeId`).
 */
export interface PublicWayfindingLegendCount {
  frameId: string;
  layerId: string;
  signTypeId: string | null;
  count: number;
}

export interface WayfindingLegendRow {
  /** `null` — указатели без типа. */
  typeId: string | null;
  /** Тип из справочника; `null` — без типа. */
  type: WayfindingSignType | null;
  /** Подпись строки; у строки без типа — `''`, её подставляет вызывающий на своём языке. */
  label: string;
  count: number;
}

/**
 * Строки легенды по счёту указателей: типы по подписи с числами по-человечески («130_M»
 * раньше «1000_M»), без типа — в конце. Тип не из справочника читается как «без типа» — так
 * же его рисует карта. Пустые строки отброшены.
 */
export function buildWayfindingLegendRows(
  counts: Iterable<{ signTypeId: string | null; count: number }>,
  signTypes: readonly WayfindingSignType[],
): WayfindingLegendRow[] {
  const typeById = new Map(signTypes.map((type) => [type.id, type]));
  const byType = new Map<string | null, number>();
  for (const { signTypeId, count } of counts) {
    if (!(count > 0)) {
      continue;
    }
    const key = signTypeId && typeById.has(signTypeId) ? signTypeId : null;
    byType.set(key, (byType.get(key) ?? 0) + count);
  }
  const rows: WayfindingLegendRow[] = [];
  for (const [typeId, count] of byType) {
    const type = typeId ? typeById.get(typeId) : undefined;
    if (type) {
      rows.push({ typeId: type.id, type, label: wayfindingLegendTypeLabel(type), count });
    }
  }
  rows.sort((a, b) => compareStringsNaturalSort(a.label, b.label));
  const untyped = byType.get(null) ?? 0;
  return untyped > 0 ? [...rows, { typeId: null, type: null, label: '', count: untyped }] : rows;
}

export interface WayfindingLegends {
  /** Легенда плана по его id; плана без указателей в словаре нет. */
  byFrame: Map<string, WayfindingLegendRow[]>;
  /** Общая легенда — по всем планам из `frameIds` (или всем, что есть в счёте). */
  summary: WayfindingLegendRow[];
}

/**
 * Легенды планов и общая. `frameIds` — планы, которые учитываются (выгрузка части альбома);
 * нет — все из счёта. Указатели вне планов (черновики) в счёт не попадают у вызывающего.
 */
export function buildWayfindingLegends(
  counts: readonly WayfindingLegendCount[],
  signTypes: readonly WayfindingSignType[],
  frameIds?: Iterable<string>,
): WayfindingLegends {
  const wanted = frameIds ? new Set(frameIds) : null;
  const perFrame = new Map<string, WayfindingLegendCount[]>();
  const all: WayfindingLegendCount[] = [];
  for (const entry of counts) {
    if (wanted && !wanted.has(entry.frameId)) {
      continue;
    }
    const list = perFrame.get(entry.frameId) ?? [];
    list.push(entry);
    perFrame.set(entry.frameId, list);
    all.push(entry);
  }
  const byFrame = new Map<string, WayfindingLegendRow[]>();
  for (const [frameId, list] of perFrame) {
    const rows = buildWayfindingLegendRows(list, signTypes);
    if (rows.length > 0) {
      byFrame.set(frameId, rows);
    }
  }
  return { byFrame, summary: buildWayfindingLegendRows(all, signTypes) };
}

/* ------------------------------------------------------------------ */
/*  Пропорции                                                          */
/* ------------------------------------------------------------------ */

/**
 * Размеры легенды. Все — от кегля строк, а кегль — доля листа плана (`wayfindingLegendMetrics`);
 * на канвасе он крупнее и умножается на ползунок «Размер легенды» (`wayfindingLegendCanvasMetrics`).
 */
export interface WayfindingLegendMetrics {
  /** Кегль строк. */
  fontSize: number;
  titleSize: number;
  subtitleSize: number;
  /** Кегль шапки столбцов. */
  headSize: number;
  /** Сторона знака типа. */
  iconSize: number;
  /** Интерлиньяж названия, которое не уместилось в строку. */
  lineHeight: number;
  /** Между строками. */
  rowGap: number;
  /** Между знаком, названием и количеством. */
  columnGap: number;
  /** Под заголовком (с подзаголовком). */
  titleGap: number;
  /** От края плана до легенды. */
  sideGap: number;
  /** Поле листа вокруг легенды. */
  margin: number;
  /** Ширина названия, дальше — перенос. */
  nameMaxWidth: number;
  /** Между столбцами строк, когда легенда не уместилась в высоту. */
  blockGap: number;
}

/** Размеры легенды от кегля строк. */
export function wayfindingLegendMetricsForFont(fontSize: number): WayfindingLegendMetrics {
  return {
    fontSize,
    titleSize: fontSize * 1.3,
    subtitleSize: fontSize * 0.9,
    headSize: fontSize * 0.85,
    iconSize: fontSize * 1.4,
    lineHeight: fontSize * 1.25,
    rowGap: fontSize * 0.7,
    // ✏️ 20.10.26: столбцы и шапка разведены чуть шире (было 0,9 и 1,1).
    columnGap: fontSize * 1.3,
    titleGap: fontSize * 1.5,
    sideGap: fontSize * 3,
    margin: fontSize * 2.5,
    nameMaxWidth: fontSize * 22,
    blockGap: fontSize * 2.5,
  };
}

/**
 * Формат листа плана в единицах листа: страница исходного PDF — так план выгрузит вектор, иначе
 * растр в пикселях. Одна функция на канвас и выгрузку: легенда на экране занимает ту же долю
 * листа, что на распечатке.
 */
export function wayfindingPlanSheetSize(frame: {
  widthPx: number;
  heightPx: number;
  pdfPage?: { viewportPt: readonly [number, number] } | null;
}): [number, number] {
  const viewport = frame.pdfPage?.viewportPt;
  if (viewport && viewport[0] > 0 && viewport[1] > 0) {
    return [viewport[0], viewport[1]];
  }
  return [Math.max(1, frame.widthPx), Math.max(1, frame.heightPx)];
}

/**
 * Размеры легенды на листе в единицах листа (пункты PDF; у растрового плана — пиксели растра).
 * Кегль — от МЕНЬШЕЙ стороны листа, как у маркеров выгрузки (`markerMetrics`): легенда занимает
 * одну и ту же долю листа на A4 и на A0.
 */
export function wayfindingLegendMetrics(widthUnits: number, heightUnits: number): WayfindingLegendMetrics {
  const base = Math.min(widthUnits, heightUnits);
  // Границы — на вырожденные листы, как у маркеров: крошечный растр не даёт кегль в пиксель.
  return wayfindingLegendMetricsForFont(Math.min(64, Math.max(7, (base > 0 ? base : 600) * 0.014)));
}

/**
 * Во сколько раз легенда на канвасе крупнее, чем на листе. В долях печати (1,4 % листа) план во
 * весь экран давал кегль в 6 px — на бумаге это нормальные 8–12 pt, а на экране не читается.
 */
export const WAYFINDING_LEGEND_CANVAS_SCALE = 1.5;

/**
 * Ползунок «Размер легенды», % (`settings.navigation.legendScale`): множитель к размеру легенды
 * на канвасе. Легенда по-прежнему в масштабе плана — растёт вместе с ним при зуме; ползунок
 * только для канваса, лист выгрузки он не трогает.
 */
export const WAYFINDING_LEGEND_SCALE_DEFAULT = 100;
export const WAYFINDING_LEGEND_SCALE_MIN = 50;
export const WAYFINDING_LEGEND_SCALE_MAX = 300;

/** Размеры легенды на канвасе в единицах листа плана: доли листа × 1,5 × ползунок. */
export function wayfindingLegendCanvasMetrics(
  widthUnits: number,
  heightUnits: number,
  scalePercent: number = WAYFINDING_LEGEND_SCALE_DEFAULT,
): WayfindingLegendMetrics {
  const sheet = wayfindingLegendMetrics(widthUnits, heightUnits);
  return wayfindingLegendMetricsForFont((sheet.fontSize * WAYFINDING_LEGEND_CANVAS_SCALE * scalePercent) / 100);
}

/** Высота строки шапки столбцов с зазором под ней. */
export function wayfindingLegendColumnHeadHeight(metrics: WayfindingLegendMetrics): number {
  return metrics.headSize * 1.3 + metrics.rowGap;
}

/** Высота всего над строками: заголовок, подзаголовок, отступ и шапка столбцов. */
export function wayfindingLegendHeaderHeight(metrics: WayfindingLegendMetrics, hasSubtitle: boolean): number {
  return (
    metrics.titleSize * 1.2 +
    (hasSubtitle ? metrics.subtitleSize * 1.4 : 0) +
    metrics.titleGap +
    wayfindingLegendColumnHeadHeight(metrics)
  );
}

/** Высота строки легенды в `lines` строк названия, без зазора под ней. */
export function wayfindingLegendRowHeight(metrics: WayfindingLegendMetrics, lines = 1): number {
  return Math.max(metrics.iconSize, lines * metrics.lineHeight);
}

/**
 * Строки по столбцам высотой `available`: подряд, пока следующая строка (с зазором `rowGap`)
 * помещается, — иначе новый столбец справа. В столбце хотя бы одна строка, даже выше
 * `available`: пропавшая строка хуже вылезшей за край. Высоты — реальные, с переносами
 * названий: одна раскладка на канвас и лист выгрузки.
 */
export function splitWayfindingLegendColumns<T>(
  rows: readonly T[],
  heightOf: (row: T) => number,
  available: number,
  rowGap: number,
): T[][] {
  const columns: T[][] = [];
  let current: T[] = [];
  let used = 0;
  for (const row of rows) {
    const height = heightOf(row);
    if (current.length > 0 && used + rowGap + height > available) {
      columns.push(current);
      current = [];
      used = 0;
    }
    used += (current.length > 0 ? rowGap : 0) + height;
    current.push(row);
  }
  if (current.length > 0) {
    columns.push(current);
  }
  return columns;
}
